// Deterministic onboarding API hooks.
// Mutations invalidate the profile query so the layout re-renders with the
// freshest completedSteps after each PATCH.
import type {
  GoalRealismCheckRequest,
  GoalRealismCheckResponse,
  OnboardingProfileEnvelope,
  UpdateActivityProfileInput,
  UpdateBodyStatsInput,
  UpdateGoalInput,
  UpdateTrainingPreferenceInput,
  UpdateTrackingModesInput,
  UserProfileAI,
} from '@onflow/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api.js';

const PROFILE_KEY = ['ai', 'onboarding', 'profile'] as const;

export function useOnboardingProfile() {
  return useQuery<UserProfileAI | null>({
    queryKey: PROFILE_KEY,
    // The API returns 200 with body `null` when the profile doesn't exist yet,
    // so we don't need any special 404 handling here — resumability code can
    // branch on `data === null` directly.
    queryFn: async () => (await api<OnboardingProfileEnvelope>('/onboarding')).profile,
    staleTime: 30_000,
  });
}

export function useUpdateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateGoalInput) =>
      api<UserProfileAI>('/onboarding/goal', { method: 'PATCH', json: input }),
    // Write the merged profile straight into the cache (the PATCH returns it)
    // so the next screen's resumability guard sees the just-completed step
    // synchronously. A background invalidate would leave completedSteps stale
    // at navigate() time and bounce the user back to this step — see the
    // OnboardingLayout prereq check.
    onSuccess: (profile) => {
      qc.setQueryData(PROFILE_KEY, profile);
      qc.invalidateQueries({ queryKey: ['nutrition-target'] });
    },
  });
}

export function useUpdateTrackingModes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTrackingModesInput) =>
      api<UserProfileAI>('/onboarding/modes', { method: 'PATCH', json: input }),
    onSuccess: (profile) => {
      qc.setQueryData(PROFILE_KEY, profile);
      qc.invalidateQueries({ queryKey: ['nutrition-target'] });
    },
  });
}

export function useUpdateBodyStats() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateBodyStatsInput) =>
      api<UserProfileAI>('/onboarding/body-stats', { method: 'PATCH', json: input }),
    onSuccess: (profile) => {
      qc.setQueryData(PROFILE_KEY, profile);
      qc.invalidateQueries({ queryKey: ['nutrition-target'] });
    },
  });
}

export function useUpdateActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateActivityProfileInput) =>
      api<UserProfileAI>('/onboarding/activity', { method: 'PATCH', json: input }),
    onSuccess: (profile) => {
      qc.setQueryData(PROFILE_KEY, profile);
      qc.invalidateQueries({ queryKey: ['nutrition-target'] });
    },
  });
}

export function useUpdateTraining() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTrainingPreferenceInput) =>
      api<UserProfileAI>('/onboarding/training', { method: 'PATCH', json: input }),
    onSuccess: (profile) => {
      qc.setQueryData(PROFILE_KEY, profile);
    },
  });
}

export function useGoalRealismCheck() {
  return useMutation({
    mutationFn: (input: GoalRealismCheckRequest) =>
      api<GoalRealismCheckResponse>('/onboarding/goal-realism-check', {
        method: 'POST',
        json: input,
      }),
  });
}

export function useCompleteOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<UserProfileAI>('/onboarding/complete', { method: 'POST', json: {} }),
    // Returns the profile with completedAt set; cache it synchronously so the
    // post-onboarding redirect doesn't race a background refetch.
    onSuccess: (profile) => {
      qc.setQueryData(PROFILE_KEY, profile);
      qc.invalidateQueries({ queryKey: ['nutrition-target'] });
      qc.invalidateQueries({ queryKey: ['billing', 'status'] });
    },
  });
}
